import React, { useEffect, useState } from 'react';
import { checkAll, checkMax, checkMoney, container, Container, Popup } from '@ivorobioff/shared';
import { Subject } from 'rxjs';
import Budget from '../../../models/Budget';
import Saving from '../../../models/Saving';
import { SavingService } from '../../../services/SavingService';
import { Controller, useForm } from 'react-hook-form';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import SavingOperationService from '../../../services/SavingOperationService';
import { OperationIntent } from '../../../models/Operation';
import { requireValue } from '../../../random/utils';

export interface PutAsidePopupProps {
  onClose: () => void;
  container: Container;
  onTransfer: (amount: number) => void;
  budget: Budget;
}

interface FormData {
  amount: number;
  savingId: string;
}

export function PutAsidePopup({ onClose, budget, onTransfer }: PutAsidePopupProps) {
  const [savings, setSavings] = useState<Saving[]>([]);
  const savingsService = container.get(SavingService);
  const savingOperationService = container.get(SavingOperationService);

  const { control, handleSubmit } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      amount: budget.availableAmount,
      savingId: ''
    }
  });

  useEffect(() => {
    savingsService.getAllAsync().then(setSavings);
  }, []);

  const canTransfer = savings.length > 0;

  return <Popup
    onHandle={() => {
      const subject = new Subject<boolean>();

      const done = (ok: boolean) => {
        subject.next(!ok);
        subject.complete();
      };

      handleSubmit(async ({ amount, savingId }) => {
        amount = Number(amount);

        const saving = savings.find((saving) => saving.id === savingId);

        requireValue(saving, `Can't find saving by "${savingId}"!`);

        Object.assign(saving, await savingOperationService.submit(saving, {
          amount,
          intent: OperationIntent.Plus,
          note: 'Put aside from the balance'
        }));

        onTransfer(amount);
        done(true);
      }, () => done(false))();

      return subject.asObservable();
    }}
    onClose={onClose}
    submitButtonTitle="Transfer"
    cancelButtonTitle="Cancel"
    submitButtonDisabled={!canTransfer}
    open={true}
    title="Put Aside">

    <form>
      <Controller name="amount" control={control}
        rules={{
          validate: checkAll(checkMoney, (v) => checkMax(v, budget.availableAmount)),
        }}
        render={({ field, fieldState: { invalid, error } }) =>
          <TextField label="Amount"
            fullWidth
            error={invalid}
            helperText={error?.message}
            {...field} />} />
      <Box mb={2} />
      <Controller name="savingId" control={control}
        rules={{
          required: `It's required!`
        }}
        render={({ field, fieldState: { invalid, error } }) =>
          <FormControl fullWidth error={invalid}>
            <InputLabel>Saving</InputLabel>
            <Select {...field}>
              {savings.map(saving => <MenuItem key={saving.id} value={saving.id}>{saving.name}</MenuItem>)}
            </Select>
            {invalid && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>} />
    </form>
  </Popup>;
}